import type { CapacityOperands } from "@hakili/contracts";
import { formatMoney, formatQuantity, t } from "@hakili/i18n";
import { Button } from "@hakili/ui";
import { AlertTriangle, Info } from "lucide-react";
import type { CSSProperties, ReactElement } from "react";

import { fill } from "./dossiers.js";
import { incompleteCauseOf } from "./incomplete-cause.js";

/**
 * `ARC2-SHEET` (D2 `analyst.js:91-110`, frames `22`, `29`, `35`; AA-AD rulings): **the quotité point's calculation sheet, as D2 draws
 * it, from the server's typed operands and nothing else** (`QUOTITE-OPERANDS`, `REMAINING-CAPACITY`). It reads what the answer carries
 * and computes nothing: a figure the server does not send is not drawn. The two numbers that place the bar and its dashed mark are the
 * door's own (`capacityUsedPct` and the 100 % of the cessible part); D2's sum on the bar is not drawn (no field, ruling: D2's sheet
 * prints none).
 *
 * - **One reading, the run's term with its basis.** « 48 mois (durée demandée) » or « 60 mois (durée maximale) ».
 * - **The third figure** « part de la capacité restante » is kept by ruling (`remainingCapacityUsedPct`, or its own cause in the server's words).
 * - **The not-computed state** dashes the operands and says « Quotité non calculée : {short cause} » on a warning card.
 */
const tenths = (value: number): string => {
  const [whole = "0", fraction = "0"] = String(value).split(".");
  return `${whole},${fraction}`;
};
/** R3 (D2 `analyst.js`): an empty figure is « − » (U+2212), the catalogue's sign, never the hyphen. */
const empty = (): string => t("bank.feuille.vide");
const natural = (value: number): string => String(value).replace(".", ",");
const rate = (value: string): string => value.replace(".", ",");
const percent = (value: number): string => `${tenths(value)}\u202f%`;

const Row = (props: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly total?: boolean;
  /** A figure above 100 %: the bad tone, colour only (nothing is computed, no sentence is added). */
  readonly bad?: boolean;
  /** `J2`: the server's CAUSE standing in the figures' column (« capacité disponible nulle »): regular, not a figure. */
  readonly cause?: boolean;
}): ReactElement => (
  <tr
    data-testid={props.id}
    {...(props.total === true ? { "data-total": "" } : {})}
    {...(props.bad === true ? { "data-tone": "bad" } : {})}
    {...(props.cause === true ? { "data-cause": "" } : {})}
  >
    <td>{props.label}</td>
    <td>{props.value}</td>
  </tr>
);

/**
 * The part of a point the sheet reads, written here so that this file imports nothing from `analyse.tsx` (which imports it:
 * a cycle between two modules is a boundaries error). `AnalysisPointView` is assignable to it.
 */
export interface SheetPoint {
  readonly code: string;
  readonly evidence:
    | {
        readonly kind: "figures";
        readonly compared: Readonly<Record<string, number | string | null>>;
        readonly operands: CapacityOperands | null;
      }
    | { readonly kind: "document" }
    | null;
}

/** The sheet for the quotité point, or `null` where the point is another or the door carries no operands (an older door). */
export function calculationSheetOf(point: SheetPoint, onPolitique?: () => void): ReactElement | null {
  if (point.code !== "quotiteCessible" || point.evidence?.kind !== "figures") return null;
  const operands: CapacityOperands | null = point.evidence.operands;
  if (operands === null) return null;
  // The wire key is the door's French label, read through the catalogue so the word is written once.
  const stored = point.evidence.compared[t("bank.feuille.quotite")];
  const storedWords = stored === undefined || stored === null ? null : String(stored);
  const computed = operands.notComputed === null;
  // D2 prints the rows as bare figures under « FCFA par mois »; the caption keeps its unit.
  const amount = (value: number | null): string => (value === null ? empty() : formatQuantity(value));
  const basis =
    operands.termBasis === "asked"
      ? t("bank.feuille.dureeDemandee")
      : operands.termBasis === "maximum"
        ? t("bank.feuille.dureeMaximale")
        : null;
  const term =
    operands.termMonths === null || basis === null
      ? null
      : fill("bank.feuille.dureeMois", { n: String(operands.termMonths), base: basis });
  const source = operands.rateSource;
  const rateLine =
    operands.ratePct === null || source === null
      ? null
      : source.kind === "highestRow"
        ? fill("bank.feuille.tauxLigneHaute", { note: source.grade, taux: rate(operands.ratePct) })
        : fill("bank.feuille.tauxMandat", { taux: rate(operands.ratePct) });
  const used = operands.capacityUsedPct;
  const exceeds = operands.capacityVerdict === "exceeds";
  const ceiling =
    operands.assignableSharePct === null ? null : `${natural(operands.assignableSharePct)}\u202f%`;
  // R4 (D2 keeps « × quotité cessible 40 % » with no salary: the ceiling is the policy's): the figure the point serves beside the operands,
  // in the server's words, never typed here and never taken from the stored quotité.
  const servedCeiling = point.evidence.compared[t("bank.feuille.plafond")];
  const ceilingWords =
    ceiling ?? (typeof servedCeiling === "string" && servedCeiling !== "" ? servedCeiling : null);
  // The bar and its mark: positions from the door's two figures (D2 `scale = max(q, 100) × 1.18`), never a figure drawn from them.
  const scale = used === null ? 100 : Math.max(used, 100) * 1.18;
  const markAt = (100 / scale) * 100;
  const barWidth = used === null ? 0 : (used / scale) * 100;
  const cause = operands.notComputed === null ? null : incompleteCauseOf(operands.notComputed);
  return (
    <div data-sheet-frame="" data-testid="bank-feuille">
      <div data-sheet="">
        <div>
          <h4 data-testid="bank-feuille-titre">
            {t("bank.feuille.feuille")} <span>{t("bank.feuille.fcfaMois")}</span>
          </h4>
          {term === null || used === null ? null : (
            <p data-sheet-lead="" data-testid="bank-feuille-duree">
              {fill("bank.feuille.tete", { duree: "\u0000", pct: percent(used) })
                .split("\u0000")
                .flatMap((part, index) => (index === 0 ? [part] : [<b key="duree">{term}</b>, part]))}
              {exceeds ? t("bank.feuille.teteDepasse") : ""}
            </p>
          )}
          {term === null || used === null || operands.termMonths === null || basis === null ? null : (
            <div data-sheet-gauge="">
              <div
                data-sheet-mark=""
                data-testid="bank-feuille-repere"
                style={{ "--x": `${String(markAt)}%` } as CSSProperties}
              />
              <div
                data-sheet-bar=""
                data-testid="bank-feuille-barre"
                data-tone={exceeds ? "bad" : "ok"}
                style={{ "--w": `${String(barWidth)}%` } as CSSProperties}
              >
                <b>{fill("bank.feuille.barre", { n: String(operands.termMonths), base: basis })}</b>
                <i>{percent(used)}</i>
                {/* D2 analyst.js:103 \`<code>\`: the sum of the commitments and the instalment, the SERVER's figure (commitmentsAndInstalmentAmount, ARC2-DATA): never added here, absent when the door sends none. */}
                {operands.commitmentsAndInstalmentAmount === null ? null : (
                  <code>{formatQuantity(operands.commitmentsAndInstalmentAmount)}</code>
                )}
              </div>
              {operands.assignableShareAmount === null ? null : (
                <p data-testid="bank-feuille-legende">
                  {fill("bank.feuille.legende", { montant: formatMoney(operands.assignableShareAmount) })}
                </p>
              )}
            </div>
          )}
          <table data-sheet-rows="">
            <tbody>
              <Row
                id="bank-feuille-revenu"
                label={t("bank.feuille.revenu")}
                value={amount(operands.retainedNetAmount)}
              />
              <Row
                id="bank-feuille-cessible"
                label={
                  ceilingWords === null
                    ? t("bank.feuille.cessibleVide")
                    : fill("bank.feuille.cessible", { pct: ceilingWords })
                }
                value={amount(operands.assignableShareAmount)}
              />
              <Row
                id="bank-feuille-engagements"
                label={t("bank.feuille.engagements")}
                value={amount(operands.retainedCommitmentsAmount)}
              />
              <Row
                id="bank-feuille-mensualite"
                label={
                  term === null || !computed
                    ? t("bank.feuille.mensualite")
                    : fill("bank.feuille.mensualiteA", { duree: term })
                }
                value={amount(operands.instalmentAmount)}
              />
              <Row
                id="bank-feuille-utilisee"
                total
                label={
                  computed && ceiling !== null
                    ? fill("bank.feuille.utiliseeFormule", { pct: ceiling })
                    : t("bank.hakili.quotiteUtilisee")
                }
                value={used === null ? empty() : percent(used)}
                bad={used !== null && exceeds}
              />
              {computed && storedWords !== null ? (
                <Row
                  id="bank-feuille-quotite"
                  total
                  label={fill("bank.feuille.quotiteTest", { pct: ceiling ?? empty() })}
                  value={storedWords}
                />
              ) : null}
            </tbody>
          </table>
          {rateLine === null ? null : (
            <p data-sheet-note="" data-testid="bank-feuille-taux">
              {rateLine}
            </p>
          )}
        </div>
        <div>
          {computed ? (
            <div data-sheet-card="info" data-testid="bank-feuille-carte">
              <Info data-sheet-icon="" width={20} height={20} strokeWidth={2} aria-hidden="true" />
              <div>
                <p>
                  <b>
                    {storedWords === null
                      ? null
                      : fill("bank.feuille.carteLit", { quotite: storedWords, pct: ceiling ?? empty() })}
                  </b>
                </p>
                {term === null ? null : (
                  <p>
                    {fill("bank.feuille.carteCapacite", {
                      duree: `${String(operands.termMonths)} mois, ${basis ?? ""}`,
                    })}
                    {exceeds ? t("bank.feuille.carteCapaciteDepasse") : "."}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div data-sheet-card="warn" data-testid="bank-feuille-non-calculee">
              <AlertTriangle data-sheet-icon="" width={20} height={20} strokeWidth={2} aria-hidden="true" />
              <div>
                <p>
                  <b>
                    {cause === null
                      ? t("bank.hakili.capaciteNonCalculee")
                      : fill("bank.feuille.carteTitreNon", { court: cause.court })}
                  </b>
                </p>
                <p>{cause === null ? (operands.notComputed?.words ?? "") : cause.phrase}</p>
                {onPolitique !== undefined && operands.notComputed?.cause === "grille" ? (
                  <p>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={onPolitique}
                      data-testid="bank-feuille-politique"
                    >
                      {t("bank.composer.politiqueLien")}
                    </Button>
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * R5 (D2 `analyst.js:84`): where the quotité point's sheet cannot be computed, the row says so instead of the stored verdict: the chip « Pas de
 * proposition » and the sub-line « Quotité non calculée : {cause}. » Read from the typed operands (`notComputed`), nothing computed here;
 * `null` where the sheet computes, or the point is another.
 */
export function sheetNotComputedLine(point: SheetPoint): string | null {
  if (point.code !== "quotiteCessible" || point.evidence?.kind !== "figures") return null;
  const notComputed = point.evidence.operands?.notComputed;
  if (notComputed === undefined || notComputed === null) return null;
  const cause = incompleteCauseOf(notComputed);
  return cause === null
    ? t("bank.hakili.capaciteNonCalculee")
    : fill("bank.feuille.carteTitreNon", { court: cause.court });
}
